/**
 * Super admin · Systems (`/admin/systems`): today's runs by outcome, the
 * stuck ones first, a workspace × Team × day grid whose cells open that day's
 * runs and whose Team names open god mode, failures grouped by stop code, and
 * the signals behind each number. Every figure names where it comes from (the
 * run table column, the Datadog metric, the Mixpanel event, the Sentry tag),
 * and the page never shows a figure the app's records cannot back. The
 * outcome mapping and the sources are `docs/ADMIN_SYSTEMS.md`.
 *
 * It is the admin area's home inside AdminShell, which owns the nav and the
 * workspace scope (`?ws=`): the Workspace chip and the scope are one value.
 * Deactivated workspaces are not read. The attention strip (incomplete
 * checkpoints, deletion requests, new feedback) moved here from Overview.
 */
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import type {
  SystemsCounts,
  SystemsDay,
  SystemsOutcome,
  SystemsOverview,
  SystemsReason,
  SystemsRunDetail,
  SystemsRunRow,
  SystemsRuntime,
  SystemsSignal,
  SystemsWorkerFace,
} from "@yagni/shared";
import {
  Fold,
  MenuItem,
  MenuLabel,
  MoreMenu,
  Pill,
  RecordHead,
  Row,
  SheetFoot,
  Stamp,
  cx,
  type PillKind,
} from "../../queue/index.js";
import { useSheet } from "../../shell/useSheet.js";
import { WorkerMark } from "../../workers/WorkerMark.js";
import { enterGodMode } from "../../lib/godMode.js";
import { useAuthSession } from "../../hooks/useWorkspaceRole.js";
import { useAdminScope } from "../../shell/AdminShell.js";
import { AttentionChip } from "./shared.js";
import {
  actOnSystemsRun,
  fetchSystemsRun,
  fetchSystemsRuns,
  useOnce,
  useSystemsOverview,
  useSystemsSignals,
  type SystemsQuery,
} from "./systemsApi.js";
import "./systems.css";

// ─── Formatting ─────────────────────────────────────────────────────────────

/** Millicents to dollars, whole above $100, cents below. */
export function money(millicents: number): string {
  const dollars = millicents / 100_000;
  if (dollars >= 100) return `$${Math.round(dollars).toLocaleString("en-US")}`;
  return `$${dollars.toFixed(2)}`;
}

export function duration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "-";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 10) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

function ago(iso: string | null, now: number): string {
  if (!iso) return "no activity yet";
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

function clock(iso: string, today: string): string {
  const at = new Date(iso);
  const time = `${String(at.getUTCHours()).padStart(2, "0")}:${String(at.getUTCMinutes()).padStart(2, "0")}`;
  return iso.slice(0, 10) === today ? time : `${dayLabel(iso.slice(0, 10), today)} ${time}`;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** `Fri 3`, or `Today`. UTC, the backend's day. */
export function dayLabel(day: string, today: string): string {
  if (day === today) return "Today";
  const at = new Date(`${day}T00:00:00.000Z`);
  return `${WEEKDAYS[at.getUTCDay()]} ${at.getUTCDate()}`;
}

function tokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

const total = (c: SystemsCounts) => c.succeeded + c.stopped + c.failed + c.blocked;

const OUTCOME_KEYS = [
  ["succeeded", "k-ok", "var(--accent-active)"],
  ["stopped", "k-st", "var(--honesty)"],
  ["failed", "k-fl", "var(--incident)"],
  ["blocked", "k-bl", "var(--accent)"],
] as const;

const PILL_OF: Record<SystemsOutcome, [string, PillKind]> = {
  running: ["Running", "live"],
  failed: ["Failed", "stop"],
  blocked: ["Blocked", "needs"],
  stopped: ["Stopped", "stop"],
  succeeded: ["Succeeded", "done"],
  skipped: ["Skipped", "done"],
};

const TONE_OF: Record<SystemsOutcome, string> = {
  running: "var(--accent-active)",
  failed: "var(--incident)",
  blocked: "var(--accent)",
  stopped: "var(--honesty)",
  succeeded: "var(--text-tertiary)",
  skipped: "var(--text-quiet)",
};

// ─── Small pieces ───────────────────────────────────────────────────────────

export function Sec({ title, fact, right, testId, children }: { title: ReactNode; fact?: ReactNode; right?: ReactNode; testId?: string; children?: ReactNode }) {
  return (
    <section className="sec" data-testid={testId}>
      <div className="sh">
        <h2>{title}</h2>
        {fact != null && <span className="fact">{fact}</span>}
        {right != null && (
          <>
            <span className="sp" />
            {right}
          </>
        )}
      </div>
      {children}
    </section>
  );
}

function Tile({ value, label, sub, tone }: { value: ReactNode; label: string; sub: ReactNode; tone?: string }) {
  return (
    <div className="tile">
      <span className={cx("n", tone)}>{value}</span>
      <span className="l">{label}</span>
      <span className="s">{sub}</span>
    </div>
  );
}

export function CellBar({ counts }: { counts: SystemsCounts }) {
  const t = total(counts);
  return (
    <span className="cellbar">
      <span className={cx("n", t === 0 && "zero")}>{t || "·"}</span>
      <span className="b">
        {t > 0 &&
          OUTCOME_KEYS.map(([key, cls]) =>
            counts[key] ? <i key={key} className={cls} style={{ width: `${(100 * counts[key]) / t}%` }} /> : null,
          )}
      </span>
    </span>
  );
}

function Bar2({ counts }: { counts: SystemsCounts }) {
  const t = total(counts);
  return (
    <div className="bar2" style={{ height: 10 }}>
      {t > 0 &&
        OUTCOME_KEYS.map(([key, cls]) =>
          counts[key] ? <i key={key} className={cls} style={{ width: `${(100 * counts[key]) / t}%` }} /> : null,
        )}
    </div>
  );
}

/** The 7-day stacked histogram, in HTML so labels never stretch. */
function Hist({ days, today }: { days: SystemsDay[]; today: string }) {
  const max = Math.max(1, ...days.map(total));
  return (
    <div className="hist" aria-label="Runs per day by outcome">
      {days.map((day) => {
        const t = total(day);
        return (
          <div className="hcol" key={day.day}>
            <span className="hv">{t || ""}</span>
            <div className="hb">
              {OUTCOME_KEYS.map(([key, , color]) =>
                day[key] ? (
                  <i key={key} style={{ height: `${Math.max(2, Math.round((100 * day[key]) / max))}%`, background: color }} />
                ) : null,
              )}
            </div>
            <span className="hl">{dayLabel(day.day, today).replace(/\s\d+$/, "")}</span>
          </div>
        );
      })}
    </div>
  );
}

function Spark({ trend, width = 70, height = 18 }: { trend: number[]; width?: number; height?: number }) {
  const max = Math.max(1, ...trend);
  const step = width / Math.max(1, trend.length);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width, height, display: "block" }} aria-hidden>
      {trend.map((v, i) => {
        const h = Math.max(2, ((height - 2) * v) / max);
        return (
          <rect
            key={i}
            x={i * step}
            y={height - h}
            width={Math.max(2, step - 3)}
            height={h}
            rx={1.5}
            fill={i === trend.length - 1 ? "var(--incident)" : "rgba(var(--ink-rgb), .22)"}
          />
        );
      })}
    </svg>
  );
}

function Face({ slug, faces, size = "sm" }: { slug: string; faces: Map<string, SystemsWorkerFace>; size?: "sm" | "lg" }) {
  const face = faces.get(slug);
  if (!face) {
    return (
      <span className="chip" title={slug} aria-hidden>
        {slug.slice(0, 1).toUpperCase()}
      </span>
    );
  }
  return <WorkerMark avatar={face.avatar} name={face.name} slug={face.slug} size={size} />;
}

function workerName(slug: string, faces: Map<string, SystemsWorkerFace>): string {
  return faces.get(slug)?.name ?? (slug === "house" ? "House machinery" : slug);
}

/** A filter chip: the name, the picked value, and a menu of what it can be. */
function FilterChip<V extends string | number>({
  name,
  value,
  options,
  onPick,
}: {
  name: string;
  value: V | null;
  options: Array<{ value: V | null; label: string }>;
  onPick: (value: V | null) => void;
}) {
  const picked = options.find((option) => option.value === value) ?? options[0];
  return (
    <MoreMenu
      align="left"
      label={name}
      trigger={({ open, toggle }) => (
        <button
          type="button"
          className={cx("chip", value !== null && "on")}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
        >
          {name} <b>{picked?.label}</b>
        </button>
      )}
    >
      <MenuLabel>{name}</MenuLabel>
      {options.map((option) => (
        <MenuItem key={String(option.value)} on={option.value === value} onSelect={() => onPick(option.value)}>
          {option.label}
        </MenuItem>
      ))}
    </MoreMenu>
  );
}

// ─── God mode ───────────────────────────────────────────────────────────────

/** View as the workspace: god mode on it, landing on the Team's queue (or Waiting on you). */
async function viewAs(workspaceId: string, deskId: string | null): Promise<string | null> {
  const result = await enterGodMode(workspaceId, deskId ? `/desks/${encodeURIComponent(deskId)}` : "/waiting");
  return result.ok ? null : result.error;
}

// ─── The sources fold ───────────────────────────────────────────────────────

const SOURCES: Array<[string, string, string, string]> = [
  ["Runs today, by outcome", "yagni.runs.finished{status,failure_reason,worker,workspace} · yagni.runs.started", "Run Completed {status, failure_reason} · Run Started", "runs.status · runs.stop_reason · runs.failure_reason · the stop gate's payload.stopReason"],
  ["Stopped", "Wright Build Finished {outcome:needs_person, stop_kind}", "yagni.work.terminal_stop{cause}", "runs.status partial, dismissed, awaiting a person"],
  ["Failed, by reason", "Sentry tags area, operation, workspace_id, desk_id · fingerprint [area, operation]", "yagni.runs.finished{failure_reason}", "stopReasonWords.ts · jobFailureCodes.ts · qaCauses.ts"],
  ["Blocked", "Work Item Transitioned {new_phase:needs_input}", "yagni.build.environment_setup_failed", "runs.status blocked · environment stop codes"],
  ["Stuck right now", "Run Started (from this release) · yagni.runs.started", "yagni.runs.duration_ms (only on finish)", "runs.status running · runs.last_activity_at · coding_events · run_events · runs.budget_seconds"],
  ["Spend", "yagni.llm.cost_millicents / sell_millicents{workspace,worker,tier,provider}", "Run Completed {cost_usd, spend_usd}", "llm_usage WHERE session_id = run id"],
  ["Tool calls", "Tool Called {tool, tool_type, agent_id}", "yagni.code.tool_calls{tool,outcome}", "runs.tool_calls · coding_events kind tool_call"],
  ["Sandbox", "none · the sandbox id rides extra, not a tag", "yagni.build.environment_*", "run_execution_attempts.sandbox_id, workflow_id"],
  ["Dark Teams", "Dark Team Detected / Recovered {desk_id, reason}", "Workspace Work Summary {teams_dark, dark_reasons}", "desk_health_state (darkTeamSweep.ts)"],
  ["Team", "Mixpanel team events and Sentry's desk_id tag carry the Team", "no metric carries desk_id: tags are enums plus workspace by policy", "runs.desk_id · llm_usage.desk_id"],
];

function SourcesFold() {
  return (
    <Fold title="Where each number comes from" count="Mixpanel · Datadog · Sentry · the run record">
      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>On this page</th>
              <th>Signal</th>
              <th>Also</th>
              <th>Record</th>
            </tr>
          </thead>
          <tbody>
            {SOURCES.map(([what, signal, also, record]) => (
              <tr key={what}>
                <td>{what}</td>
                <td className="fact" style={{ whiteSpace: "normal" }}>{signal}</td>
                <td className="fact" style={{ whiteSpace: "normal" }}>{also}</td>
                <td className="fact" style={{ whiteSpace: "normal" }}>{record}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="fact" style={{ whiteSpace: "normal" }}>
        Outcomes: succeeded is status complete; stopped is a stop that waits for a person (partial, dismissed, awaiting a
        plan approval or a skill grant); failed is status failed; blocked is status blocked or a stop whose code is of the
        environment family; running is scoping or running; skipped runs did no work and are counted apart. A developer's
        YAGNI Code session is not an agent run and is not counted.
      </p>
      <div className="stopbox" style={{ marginTop: 4 }}>
        <div className="h">
          <Stamp>gaps</Stamp>Three things this page needed that nothing sent, and where they stand
        </div>
        <p>
          1. A run <b>started</b> event: closed. Run Started (Mixpanel) and yagni.runs.started (Datadog) fire on every run
          row written from this release on; running and stuck are still read from the runs table, which is the record.
          2. <b>desk_id</b>: closed on Sentry, where captureOperationalError tags the Team whenever one is in scope.
          Datadog metrics stay without it by the cardinality policy. 3. A per-run <b>tool-call count</b>: closed for runs
          from this release (runs.tool_calls, counted as each tool call is written); older runs count their tool_call coding
          events. The in-process agent loop's tool_executions are keyed by turn and still do not name a run.
        </p>
      </div>
    </Fold>
  );
}

// ─── The sheets ─────────────────────────────────────────────────────────────

export function RunRowLine({ run, faces, today, now, onOpen, tagMode }: {
  run: SystemsRunRow;
  faces: Map<string, SystemsWorkerFace>;
  today: string;
  now: number;
  onOpen: () => void;
  tagMode: "place" | "outcome";
}) {
  const live = run.outcome === "running";
  const state = live
    ? [
        `running ${duration(run.durationMs)}`,
        `last activity ${ago(run.lastEventAt, now)}`,
        run.toolCalls !== null ? `${run.toolCalls} tool calls` : null,
        money(run.sellMillicents),
        run.tier,
        run.runtime,
        run.sandboxId,
      ]
    : [run.code ?? run.status, duration(run.durationMs), money(run.sellMillicents), `attempt ${run.attemptNo}`];
  const tag =
    tagMode === "place"
      ? { label: `${run.workspaceName} › ${run.deskName ?? "No Team"}`, color: "var(--text-tertiary)" }
      : { label: PILL_OF[run.outcome][0], color: TONE_OF[run.outcome] };
  return (
    <Row
      glyph={<Face slug={run.worker} faces={faces} />}
      rowKey={run.itemKey ?? run.id.slice(0, 8)}
      title={run.title}
      tag={tag}
      state={state.filter(Boolean).join(" · ")}
      when={clock(run.startedAt, today)}
      verb={live ? "Open" : "Why"}
      onOpen={onOpen}
    />
  );
}

export function RunSheet({ runId, faces, today, onChanged }: {
  runId: string;
  faces: Map<string, SystemsWorkerFace>;
  today: string;
  onChanged: () => void;
}) {
  const { data, error, reload } = useOnce<SystemsRunDetail>(() => fetchSystemsRun(runId), runId);
  const [note, setNote] = useState<{ text: string; honest: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const now = Date.now();

  if (error) return <div className="sbody"><p className="fact">{error}</p></div>;
  if (!data) return <div className="sbody"><p className="fact">Reading the run</p></div>;
  const run = data.run;
  const live = run.outcome === "running";
  const [pillWord, pillKind] = PILL_OF[run.outcome];

  const act = async (which: "stop" | "again") => {
    if (which === "stop" && !window.confirm("Stop this run? The customer sees it stopped on their queue.")) return;
    setBusy(true);
    const result = await actOnSystemsRun(run.id, which);
    setBusy(false);
    setNote(result.ok
      ? { text: which === "stop" ? "Stopped. The record names you." : "A fresh attempt is starting. The record names you.", honest: false }
      : { text: result.error, honest: true });
    if (result.ok) {
      onChanged();
      reload();
    }
  };
  const view = async () => {
    const failed = await viewAs(run.workspaceId, run.deskId);
    if (failed) setNote({ text: `God mode did not open: ${failed}`, honest: true });
  };
  const open = (url: string | null) => url && window.open(url, "_blank", "noopener");

  return (
    <div className="systems-sheet" style={{ display: "contents" }} data-testid="systems-run-sheet">
      <RecordHead
        recordKey={run.id.slice(0, 8)}
        kick={
          <span className="face">
            <Face slug={run.worker} faces={faces} />
            <span className="fact">{run.kind}</span>
          </span>
        }
        title={run.title}
        meta={
          <>
            <Pill kind={pillKind}>{pillWord}</Pill>
            <span className="chip">{run.workspaceName} › {run.deskName ?? "No Team"}</span>
            {run.itemKey && <span className="chip">{run.itemKey}</span>}
            {run.tier && <span className="chip">{run.tier}</span>}
            <span className="chip">{run.runtime}</span>
            <span className="fact" style={{ marginLeft: "auto", fontSize: 11.5 }}>
              started {clock(run.startedAt, today)} · {duration(run.durationMs)}
            </span>
          </>
        }
        more={
          <>
            <MenuItem onSelect={() => void navigator.clipboard?.writeText(run.id)}>Copy run id</MenuItem>
            {data.sentryUrl && <MenuItem onSelect={() => open(data.sentryUrl)}>Sentry · this workspace</MenuItem>}
            {data.datadogUrl && <MenuItem onSelect={() => open(data.datadogUrl)}>Datadog logs · this run</MenuItem>}
            {data.mixpanelUrl && <MenuItem onSelect={() => open(data.mixpanelUrl)}>Mixpanel</MenuItem>}
          </>
        }
      />
      <div className="sbody">
        {live ? (
          <div className="nowcard">
            <div className="nh">
              <span className="ldot" />
              <span className="label" style={{ color: "var(--accent-active)" }}>Right now</span>
              <span className="fact">last activity {ago(run.lastEventAt, now)}</span>
            </div>
            <div className="nowline">
              <span className="v">{run.stuck ? "stuck" : "running"}</span>
              <code>
                {run.toolCalls ?? "?"} tool calls · {run.llmCalls} LLM calls · {tokens(run.tokens)} tokens · {money(run.sellMillicents)} sell
              </code>
            </div>
            {run.stuck && (
              <p style={{ fontSize: 12.5, color: "var(--honesty)", lineHeight: 1.5 }}>
                Quiet for ten minutes or more, or past 80% of its {run.budgetSeconds ? duration(run.budgetSeconds * 1000) : ""} budget.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="stopbox">
              <div className="h">
                <Stamp>{run.status}</Stamp>
                {run.code ?? "no code stored"}
                {run.cls ? ` · ${run.cls}` : ""}
              </div>
              <p>{run.why ?? "No stop words are stored for this run."}</p>
            </div>
            <div className="blk">
              <div className="bh"><span className="label">What would fix it</span></div>
              <p style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.55 }}>
                {run.fix ?? "The stop's gate carries no fix hint for this code."}
              </p>
            </div>
          </>
        )}
        <dl className="receipt">
          <dt>Workspace</dt>
          <dd>
            {run.workspaceName} › {run.deskName ?? "No Team"}{" "}
            <button type="button" className="mono-link" onClick={() => void view()}>view as workspace</button>
          </dd>
          <dt>Worker</dt>
          <dd>{workerName(run.worker, faces)} · {run.kind} · attempt {run.attemptNo}</dd>
          <dt>Model</dt>
          <dd>{run.tier ?? "no tier stored"} · {run.llmCalls} LLM calls · {tokens(run.tokens)} tokens</dd>
          <dt>Spend</dt>
          <dd>
            {money(run.sellMillicents)} sell · {money(run.costMillicents)} cost
            <span className="fact">llm_usage WHERE session_id = run id</span>
          </dd>
          <dt>Runtime</dt>
          <dd>
            {run.runtime}
            {run.sandboxId && <> · sandbox <span className="mono">{run.sandboxId}</span></>}
            {run.workflowId && <> · <span className="mono">{run.workflowId}</span></>}
          </dd>
          <dt>Tool calls</dt>
          <dd>
            {run.toolCalls ?? "not read"}
            <span className="fact">runs.tool_calls, else the run's tool_call coding events</span>
          </dd>
          <dt>Sentry</dt>
          <dd>
            {data.sentryUrl ? <a href={data.sentryUrl} target="_blank" rel="noreferrer">issues for this workspace</a> : <span className="fact">no Sentry org configured here</span>}
            <span className="fact">tags area, operation, workspace_id, desk_id · fingerprint [area, operation]</span>
          </dd>
          <dt>Datadog</dt>
          <dd>
            {data.datadogUrl ? <a href={data.datadogUrl} target="_blank" rel="noreferrer">logs for this run</a> : <span className="fact">Datadog is not wired on this deployment</span>}
            <span className="fact">yagni.runs.started · yagni.runs.finished · yagni.llm.attempts</span>
          </dd>
        </dl>
        <div>
          <Fold
            title={`Mixpanel · ${data.mixpanel.event}`}
            count={data.mixpanel.sent ? (live ? "as sent at start" : "as sent") : "not sent for this run"}
          >
            <pre className="codebox">{JSON.stringify({ event: data.mixpanel.event, ...data.mixpanel.properties }, null, 2)}</pre>
            {!data.mixpanel.sent && (
              <p className="fact" style={{ whiteSpace: "normal" }}>
                {data.mixpanel.event === "Run Started"
                  ? "This run started before Run Started existed (2026-10-10); the payload is what it would carry."
                  : "The run record could not be read, so nothing is shown as sent."}
              </p>
            )}
          </Fold>
          <Fold title="Run events" count={`last ${data.events.length}`}>
            {data.events.length === 0 ? (
              <p className="fact">No run events or coding events are stored for this run.</p>
            ) : (
              <div className="trail">
                {data.events.map((event, index) => (
                  <div key={`${event.at}-${index}`}>
                    <span className="t">{clock(event.at, today)}</span>
                    <span className="v">{event.kind.replace(/_.*/, "")}</span>
                    <span>{event.summary}</span>
                  </div>
                ))}
              </div>
            )}
          </Fold>
          {data.sameReason && (
            <Fold title="Same reason, 7 days" count={String(data.sameReason.runs)}>
              <p>{data.sameReason.note}</p>
            </Fold>
          )}
        </div>
      </div>
      {note && <p className={cx("act-note", note.honest && "honest")}>{note.text}</p>}
      {live ? (
        <SheetFoot
          why={<>Stopping ends attempt {run.attemptNo} and hands the ticket to you. <b>The customer sees it stopped on their queue.</b></>}
          secondary={{ label: "View as workspace", onClick: () => void view() }}
          primary={{ label: "Stop run", onClick: () => void act("stop"), disabled: busy }}
        />
      ) : (
        <SheetFoot
          why={<>Retrying starts a fresh attempt on the same line, as the customer could from their queue. <b>The record names you; the run works as the Team's accountable member.</b></>}
          secondary={{ label: "View as workspace", onClick: () => void view() }}
          primary={{ label: "Retry", onClick: () => void act("again"), disabled: busy || run.outcome === "succeeded" }}
        />
      )}
    </div>
  );
}

function RunList({ runs, faces, today, tagMode, onOpenRun, empty }: {
  runs: SystemsRunRow[] | null;
  faces: Map<string, SystemsWorkerFace>;
  today: string;
  tagMode: "place" | "outcome";
  onOpenRun: (id: string) => void;
  empty: string;
}) {
  const now = Date.now();
  if (runs === null) return <p className="fact">Reading the runs</p>;
  if (runs.length === 0) return <p className="fact">{empty}</p>;
  return (
    <div className="rows">
      {runs.map((run) => (
        <RunRowLine key={run.id} run={run} faces={faces} today={today} now={now} tagMode={tagMode} onOpen={() => onOpenRun(run.id)} />
      ))}
    </div>
  );
}

function DaySheet({ workspaceId, workspaceName, deskId, deskName, day, counts, today, faces, onOpenRun }: {
  workspaceId: string;
  workspaceName: string;
  deskId: string | null;
  deskName: string;
  day: string;
  counts: SystemsCounts;
  today: string;
  faces: Map<string, SystemsWorkerFace>;
  onOpenRun: (id: string) => void;
}) {
  const { data, error } = useOnce(
    () => fetchSystemsRuns({ day, workspaceId, deskId: deskId ?? "none", limit: 100 }),
    `${workspaceId}|${deskId}|${day}`,
  );
  const [note, setNote] = useState<string | null>(null);
  const t = total(counts);
  const sell = (data ?? []).reduce((sum, run) => sum + run.sellMillicents, 0);
  const cost = (data ?? []).reduce((sum, run) => sum + run.costMillicents, 0);
  const view = async (toTeam: boolean) => {
    const failed = await viewAs(workspaceId, toTeam ? deskId : null);
    if (failed) setNote(`God mode did not open: ${failed}`);
  };
  return (
    <div className="systems-sheet" style={{ display: "contents" }} data-testid="systems-day-sheet">
      <RecordHead
        recordKey={dayLabel(day, today).toUpperCase()}
        title={`${workspaceName} › ${deskName}`}
        noExpand
        meta={
          <>
            <span className="chip">{t} runs</span>
            <span className="chip" style={{ color: "var(--accent-active)" }}>{counts.succeeded} succeeded</span>
            <span className="chip" style={{ color: "var(--honesty)" }}>{counts.stopped} stopped</span>
            <span className="chip" style={{ color: "var(--incident)" }}>{counts.failed} failed</span>
            <span className="chip" style={{ color: "var(--accent)" }}>{counts.blocked} blocked</span>
          </>
        }
      />
      <div className="sbody">
        <Bar2 counts={counts} />
        <div className="blk">
          <div className="bh">
            <span className="label">Runs</span>
            <span className="fact">every run on record that day, newest first</span>
          </div>
          {error ? <p className="fact">{error}</p> : (
            <RunList runs={data} faces={faces} today={today} tagMode="outcome" onOpenRun={onOpenRun} empty="No runs on record for this Team that day." />
          )}
        </div>
        <div>
          <Fold title="Spend of these runs" count={data ? money(sell) : ""}>
            <p>
              {money(sell)} sell · {money(cost)} cost across {data?.length ?? 0} runs.{" "}
              <span className="fact">llm_usage by session_id = run id · yagni.llm.sell_millicents{"{workspace}"}</span>
            </p>
          </Fold>
          <Fold title="What Mixpanel has" count="">
            <p>
              Run Completed for each settled run listed here ({t}), with desk_id; Run Started for each run written from
              2026-10-10 on. Group: workspace.
            </p>
          </Fold>
        </div>
      </div>
      {note && <p className="act-note honest">{note}</p>}
      <SheetFoot
        why={<>Opens their queue in god mode, read-only until you switch to Act. <b>Acting there is attributed to you.</b></>}
        secondary={{ label: "View as workspace", onClick: () => void view(false) }}
        primary={deskId ? { label: "Open the Team", onClick: () => void view(true) } : undefined}
      />
    </div>
  );
}

const CAPTURED_WHERE: Record<string, string> = {
  provider: "Sentry: [LLM] issues · Datadog: yagni.llm.attempts{error_class:rate_limited} · yagni.failover.throttled",
  environment: "Sentry: [Wright] supervised_execution_no_candidate · Datadog: yagni.build.environment_setup_failed",
  timeout: "Sentry: [Wright] supervised_execution_no_candidate · Mixpanel: Wright Build Finished {stop_kind}",
  build: "Sentry: [Wright] supervised_execution_no_candidate · Mixpanel: Wright Build Finished {stop_kind}",
};

function ReasonSheet({ reason, days, today, faces, links, onOpenRun }: {
  reason: SystemsReason;
  days: string[];
  today: string;
  faces: Map<string, SystemsWorkerFace>;
  links: SystemsOverview["links"];
  onOpenRun: (id: string) => void;
}) {
  const { data, error } = useOnce(() => fetchSystemsRuns({ code: reason.code, days: days.length, limit: 50 }), reason.code);
  const max = Math.max(1, ...reason.trend);
  const width = 40 * reason.trend.length;
  const open = (url: string | null) => url && window.open(url, "_blank", "noopener");
  return (
    <div className="systems-sheet" style={{ display: "contents" }} data-testid="systems-reason-sheet">
      <RecordHead
        recordKey={reason.cls.toUpperCase()}
        title={reason.code}
        noExpand
        meta={
          <>
            <Pill kind="stop">{reason.runs} runs · {days.length} days</Pill>
            <span className="chip">{reason.workspaces.join(" · ")}</span>
            <span className="fact" style={{ marginLeft: "auto", fontSize: 11.5 }}>last {clock(reason.lastAt, today)}</span>
          </>
        }
      />
      <div className="sbody">
        <div className="stopbox">
          <div className="h"><Stamp>{reason.cls}</Stamp>What it means</div>
          <p>{reason.note}</p>
        </div>
        <div className="blk">
          <div className="bh"><span className="label">By day</span></div>
          <svg className="dayspark" viewBox={`0 0 ${width} 40`} preserveAspectRatio="none" aria-hidden>
            {reason.trend.map((v, i) => {
              const h = Math.max(2, (32 * v) / max);
              return <rect key={i} x={i * 40 + 6} y={36 - h} width={28} height={h} rx={2} fill={i === reason.trend.length - 1 ? "var(--incident)" : "rgba(var(--ink-rgb), .2)"} />;
            })}
          </svg>
          <div className="legend">
            {days.map((day) => (
              <span key={day} style={{ flex: 1, justifyContent: "center" }}>{dayLabel(day, today).replace(/\s\d+$/, "")}</span>
            ))}
          </div>
        </div>
        <div className="blk">
          <div className="bh">
            <span className="label">Runs</span>
            <span className="fact">{data ? `${data.length} listed · ${reason.runs} in the window` : ""}</span>
          </div>
          {error ? <p className="fact">{error}</p> : (
            <RunList runs={data} faces={faces} today={today} tagMode="place" onOpenRun={onOpenRun} empty="No runs listed." />
          )}
        </div>
        <div>
          <Fold title="Where it is captured" count="">
            <p>
              {CAPTURED_WHERE[reason.cls] ?? "Sentry: the capture's area and operation"} · Datadog:
              yagni.runs.finished{"{failure_reason}"} · Mixpanel: Run Completed {"{failure_reason}"}. No run stores a
              link to its Sentry issue, so none is shown.
            </p>
          </Fold>
        </div>
      </div>
      <SheetFoot
        why={<>The issue and the metric live in Sentry and Datadog. <b>This page links; it does not guess an issue.</b></>}
        secondary={links.datadog ? { label: "Open in Datadog", onClick: () => open(links.datadog) } : undefined}
        primary={links.sentry ? { label: "Open in Sentry", onClick: () => open(links.sentry) } : undefined}
      />
    </div>
  );
}

// ─── The page ───────────────────────────────────────────────────────────────

const OUTCOME_OPTIONS: Array<{ value: SystemsOutcome | null; label: string }> = [
  { value: null, label: "all" },
  { value: "succeeded", label: "succeeded" },
  { value: "stopped", label: "stopped" },
  { value: "failed", label: "failed" },
  { value: "blocked", label: "blocked" },
  { value: "skipped", label: "skipped" },
];

const RUNTIME_OPTIONS: Array<{ value: SystemsRuntime | null; label: string }> = [
  { value: null, label: "e2b + in-process" },
  { value: "e2b", label: "e2b" },
  { value: "in-process", label: "in-process" },
];

export function SystemsPage({
  initialQuery,
  onScope,
}: { initialQuery?: Partial<SystemsQuery>; onScope?: (workspaceId: string | null) => void } = {}) {
  const [query, setQuery] = useState<SystemsQuery>({ days: 7, ...initialQuery });
  const overview = useSystemsOverview(query);
  const signals = useSystemsSignals();
  const sheet = useSheet();
  const [pageNote, setPageNote] = useState<string | null>(null);
  const data = overview.data;
  const faces = useMemo(() => new Map((data?.workers ?? []).map((face) => [face.slug, face])), [data?.workers]);
  const set = (patch: Partial<SystemsQuery>) => {
    if (onScope && "workspaceId" in patch && patch.workspaceId !== query.workspaceId) {
      onScope(patch.workspaceId ?? null);
      return;
    }
    setQuery((was) => ({ ...was, ...patch }));
  };

  if (!data) {
    return (
      <div className="page systems" data-testid="admin-systems">
        <Header data={null} />
        <p className={cx("status-line", overview.error && "honest")}>{overview.error ?? "Reading every workspace"}</p>
      </div>
    );
  }

  // `today` labels days (the real one reads "Today"); `data.day` is the day the tiles count.
  const today = data.asOf.slice(0, 10);
  const nowMs = Date.parse(data.asOf);
  const pickableDays = Array.from({ length: query.days }, (_, index) =>
    new Date(Date.parse(`${today}T00:00:00.000Z`) - index * 86_400_000).toISOString().slice(0, 10),
  ).slice(1);
  const openRun = (id: string) =>
    sheet.open(<RunSheet key={id} runId={id} faces={faces} today={today} onChanged={() => void overview.refresh()} />);
  const openDay = (ws: { workspaceId: string; workspaceName: string }, team: { deskId: string | null; deskName: string }, cell: SystemsDay) =>
    sheet.open(
      <DaySheet
        key={`${ws.workspaceId}|${team.deskId}|${cell.day}`}
        workspaceId={ws.workspaceId}
        workspaceName={ws.workspaceName}
        deskId={team.deskId}
        deskName={team.deskName}
        day={cell.day}
        counts={cell}
        today={today}
        faces={faces}
        onOpenRun={openRun}
      />,
    );
  const openReason = (reason: SystemsReason) =>
    sheet.open(
      <ReasonSheet key={reason.code} reason={reason} days={data.days.map((d) => d.day)} today={today} faces={faces} links={data.links} onOpenRun={openRun} />,
    );
  const god = async (workspaceId: string, deskId: string | null) => {
    const failed = await viewAs(workspaceId, deskId);
    if (failed) setPageNote(`God mode did not open: ${failed}`);
  };

  const t = data.today;
  const live = [...data.stuck, ...data.running];
  const teamsOfWorkspace = data.filters.teams.filter((team) => !query.workspaceId || team.workspaceId === query.workspaceId);
  const kindMedians = [
    t.medianByKind.build !== null ? `builds ${duration(t.medianByKind.build)}` : null,
    t.medianByKind.plan !== null ? `plans ${duration(t.medianByKind.plan)}` : null,
    t.medianByKind.review !== null ? `reviews ${duration(t.medianByKind.review)}` : null,
    t.medianByKind.qa !== null ? `QA ${duration(t.medianByKind.qa)}` : null,
  ].filter(Boolean);
  const windowTotal = (cells: SystemsDay[]) => cells.reduce((sum, cell) => sum + total(cell), 0);

  return (
    <div className="page systems" data-testid="admin-systems">
      <Header data={data} />
      <Attention />
      <div className="rhythm" role="toolbar" aria-label="Filters">
        <FilterChip
          name="Day"
          value={query.day ?? null}
          options={[{ value: null, label: "Today" }, ...pickableDays.map((day) => ({ value: day, label: dayLabel(day, today) }))]}
          onPick={(day) => set({ day })}
        />
        <FilterChip
          name="Workspace"
          value={query.workspaceId ?? null}
          options={[{ value: null, label: `all ${data.filters.workspaces.length} live` }, ...data.filters.workspaces.map((ws) => ({ value: ws.id, label: ws.name }))]}
          onPick={(workspaceId) => set({ workspaceId, deskId: null })}
        />
        <FilterChip
          name="Team"
          value={query.deskId ?? null}
          options={[{ value: null, label: "all" }, ...teamsOfWorkspace.map((team) => ({ value: team.id, label: team.name }))]}
          onPick={(deskId) => set({ deskId })}
        />
        <FilterChip
          name="Worker"
          value={query.worker ?? null}
          options={[{ value: null, label: "all" }, ...data.filters.workers.map((slug) => ({ value: slug, label: workerName(slug, faces) }))]}
          onPick={(worker) => set({ worker })}
        />
        <FilterChip name="Outcome" value={query.outcome ?? null} options={OUTCOME_OPTIONS} onPick={(outcome) => set({ outcome })} />
        <FilterChip name="Runtime" value={query.runtime ?? null} options={RUNTIME_OPTIONS} onPick={(runtime) => set({ runtime })} />
        <span className="fact picked">
          <span className="ldot" style={{ display: "inline-block", verticalAlign: "middle", marginRight: 6 }} />
          live · refreshes every 30s{overview.error ? ` · last read failed: ${overview.error}` : ""}
        </span>
      </div>
      {pageNote && <p className="status-line honest">{pageNote}</p>}

      <Sec
        title={`${dayLabel(data.day, today)}, every live workspace`}
        fact="a run is one Agent attempt: plan, build, review, QA or a job line firing · UTC days"
        testId="systems-today"
      >
        <div className="tiles">
          <Tile value={t.runs} label="Runs" sub="runs rows started on the day" />
          <Tile value={t.succeeded} tone="green" label="Succeeded" sub="status complete" />
          <Tile value={t.stopped} tone="amber" label="Stopped" sub="partial · dismissed · awaiting a person" />
          <Tile value={t.failed} tone="red" label="Failed" sub="status failed · failure_reason" />
          <Tile value={t.blocked} tone="blue" label="Blocked" sub="blocked · environment stop codes" />
          <Tile
            value={<>{t.running} <span style={{ fontSize: 13, color: "var(--incident)" }}>{t.stuck} stuck</span></>}
            label="Running now"
            sub="quiet 10m or past 80% of budget is stuck"
          />
          <Tile value={duration(t.medianRunMs)} tone="quiet" label="Median run" sub={kindMedians.join(" · ") || "nothing settled yet"} />
          <Tile
            value={money(t.spend.sellMillicents)}
            label="Spend on the day"
            sub={`sell · cost ${money(t.spend.costMillicents)}${t.spend.peakShare !== null ? ` · peak ${Math.round(t.spend.peakShare * 100)}%` : ""} · llm_usage`}
          />
        </div>
        {t.skipped > 0 && <p className="fact">{t.skipped} skipped runs did no work and are not in the tiles.</p>}
        <div className="legend">
          {OUTCOME_KEYS.map(([key, cls]) => (
            <span key={key}><i className={cls} />{key}</span>
          ))}
          <span style={{ marginLeft: "auto" }}>last {data.days.length} days, every workspace</span>
        </div>
        <Hist days={data.days} today={today} />
      </Sec>

      <Sec
        title="Stuck right now"
        fact="running with no activity for 10 minutes or more, or past 80% of budget · the thing to look at first"
        right={<span className="fact">{data.stuck.length} of {live.length} running</span>}
        testId="systems-stuck"
      >
        {live.length === 0 ? (
          <div className="empty dashed tight"><p>Nothing is running right now.</p></div>
        ) : (
          <div className="rows">
            {live.map((run) => (
              <RunRowLine key={run.id} run={run} faces={faces} today={today} now={nowMs} tagMode="place" onOpen={() => openRun(run.id)} />
            ))}
          </div>
        )}
      </Sec>

      <Sec
        title="By workspace and Team"
        fact="runs a day · open a cell for that day's runs · open a Team name to view as that workspace"
        right={
          <span className="seg" role="group" aria-label="Window">
            {([7, 14, 30] as const).map((days) => (
              <button key={days} type="button" className={cx(query.days === days && "on")} onClick={() => set({ days })}>
                {days} days
              </button>
            ))}
          </span>
        }
        testId="systems-grid"
      >
        {data.grid.length === 0 ? (
          <div className="empty dashed tight"><p>No runs on record in the window.</p></div>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>Workspace › Team</th>
                  {data.days.map((day) => (
                    <th key={day.day} className={cx("num", day.day === data.day && "today")}>{dayLabel(day.day, today)}</th>
                  ))}
                  <th className="num">{data.days.length} days</th>
                </tr>
              </thead>
              <tbody>
                {data.grid.map((ws) => (
                  <WorkspaceRows
                    key={ws.workspaceId}
                    ws={ws}
                    today={today}
                    windowTotal={windowTotal}
                    onCell={(team, cell) => openDay(ws, team, cell)}
                    onTeam={(deskId) => void god(ws.workspaceId, deskId)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Sec>

      <Sec
        title={`Failures by reason, ${data.days.length} days`}
        fact="stop codes from the gate, the run and the vocabularies · one row per code · open for the runs and the fix"
        testId="systems-reasons"
      >
        {data.reasons.length === 0 ? (
          <div className="empty dashed tight"><p>No run stopped, failed or was blocked in the window.</p></div>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>Reason</th>
                  <th className="num">Runs</th>
                  <th>Trend</th>
                  <th>Last</th>
                  <th>Workspaces</th>
                  <th>Sentry</th>
                </tr>
              </thead>
              <tbody>
                {data.reasons.map((reason) => (
                  <tr key={reason.code} data-rec={reason.code} onClick={() => openReason(reason)}>
                    <td>
                      <span className="mono-code">{reason.code}</span>
                      <span className="sub">{reason.cls}</span>
                    </td>
                    <td className="num" style={{ fontWeight: 600 }}>{reason.runs}</td>
                    <td><Spark trend={reason.trend} /></td>
                    <td className="fact">{clock(reason.lastAt, today)}</td>
                    <td className="fact">{reason.workspaces.join(", ")}</td>
                    <td>
                      {reason.sentryIssue ? (
                        <a className="mono-link" href={reason.sentryIssue} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>issue</a>
                      ) : (
                        <span className="fact">no stored link</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Sec>

      <Sec title="Signals" fact="what the app itself recorded, beside the Datadog metric or monitor that watches the same thing" testId="systems-signals">
        {signals.error && <p className="status-line honest">{signals.error}</p>}
        <div className="sig">
          {(signals.data ?? []).map((signal) => (
            <SignalCard key={signal.key} signal={signal} />
          ))}
        </div>
        {data.dark.length > 0 && (
          <div className="dashed darkline" data-testid="systems-dark">
            <b>Dark Teams · {data.dark.length}</b> ·{" "}
            {data.dark.map((dark) => `${dark.workspaceName} › ${dark.deskName} (${dark.reason}, since ${dark.since.slice(0, 10)})`).join(" · ")}
            <span className="fact">Mixpanel Dark Team Detected / Recovered · desk_health_state</span>
          </div>
        )}
      </Sec>

      <SourcesFold />
    </div>
  );
}

function SignalCard({ signal }: { signal: SystemsSignal }) {
  const label = !signal.stored ? "not stored" : signal.state === "ok" ? "ok" : signal.state === "warn" ? "watch" : "firing";
  return (
    <div className="sigc" data-testid={`signal-${signal.key}`}>
      <div className="h">
        {signal.title}
        <span className={cx("state", signal.state)}>{label}</span>
      </div>
      <p>{signal.text}</p>
      <div className="src">{signal.source}</div>
    </div>
  );
}

function WorkspaceRows({ ws, today, windowTotal, onCell, onTeam }: {
  ws: SystemsOverview["grid"][number];
  today: string;
  windowTotal: (cells: SystemsDay[]) => number;
  onCell: (team: { deskId: string | null; deskName: string }, cell: SystemsDay) => void;
  onTeam: (deskId: string) => void;
}) {
  const days = ws.teams[0]?.cells.map((cell) => cell.day) ?? [];
  const subtotal = days.map((day, index) =>
    ws.teams.reduce((sum, team) => sum + total(team.cells[index] ?? { succeeded: 0, stopped: 0, failed: 0, blocked: 0 }), 0),
  );
  return (
    <>
      <tr className="wsrow">
        <td>{ws.workspaceName}</td>
        {subtotal.map((n, index) => (
          <td key={days[index]} className="num">{n || <span className="quietdot">·</span>}</td>
        ))}
        <td className="num">{subtotal.reduce((a, b) => a + b, 0)}</td>
      </tr>
      {ws.teams.map((team) => (
        <tr key={team.deskId ?? "none"}>
          <td className="team">
            {team.deskId ? (
              <button type="button" className="ref quiet" title="View as this workspace, on this Team's queue" onClick={() => onTeam(team.deskId!)}>
                {team.deskName}
              </button>
            ) : (
              <span className="fact">{team.deskName}</span>
            )}
            {team.dark && <span className="sub dark">dark · {team.dark.reason}</span>}
          </td>
          {team.cells.map((cell) => (
            <td
              key={cell.day}
              className="num"
              data-cell={`${team.deskId ?? "none"}|${cell.day}`}
              aria-label={`${team.deskName}, ${dayLabel(cell.day, today)}: ${total(cell)} runs`}
              role="button"
              tabIndex={0}
              onClick={() => onCell(team, cell)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onCell(team, cell);
                }
              }}
            >
              <CellBar counts={cell} />
            </td>
          ))}
          <td className="num">{windowTotal(team.cells)}</td>
        </tr>
      ))}
    </>
  );
}

function Header({ data }: { data: SystemsOverview | null }) {
  const release = data?.release;
  const links = data?.links;
  const facts: ReactNode[] = [
    <span key="app">{release?.app ?? "this deployment"}{release?.region ? ` · fly ${release.region}` : ""}</span>,
    release?.sha ? (
      <span key="rel">release <b>{release.sha.slice(0, 7)}</b>{release.deployedAt ? ` since ${release.deployedAt.slice(11, 16)} UTC` : ""}</span>
    ) : (
      <span key="rel">release not stamped on this machine</span>
    ),
    <span key="as">read {data ? data.asOf.slice(11, 19) : "now"} UTC</span>,
  ];
  const style: CSSProperties = { textDecoration: "none" };
  return (
    <div className="qhd">
      <div className="top">
        <div className="title">
          <div className="label kick">Super admin</div>
          <h1>Systems</h1>
          <div className="sub">{facts}</div>
        </div>
        <div className="acts">
          {links?.datadog && <a className="btn secondary sm" style={style} href={links.datadog} target="_blank" rel="noreferrer">Open in Datadog</a>}
          {links?.sentry && <a className="btn secondary sm" style={style} href={links.sentry} target="_blank" rel="noreferrer">Sentry issues</a>}
          {links?.mixpanel && <a className="btn secondary sm" style={style} href={links.mixpanel} target="_blank" rel="noreferrer">Mixpanel</a>}
        </div>
      </div>
    </div>
  );
}

interface AttentionCounts {
  incompleteCheckpoints: number;
  pendingDeletionRequests: number;
  newFeedback: number;
}

/** What waits on an operator, each a link to where it resolves. Silent until read. */
function Attention() {
  const read = useOnce(async () => {
    const res = await fetch("/api/admin/stats/overview?days=7", { credentials: "include" });
    if (!res.ok) throw new Error(`The attention read failed (${res.status})`);
    return ((await res.json()) as { attention?: AttentionCounts }).attention ?? null;
  }, "attention");
  const attention = read.data;
  if (!attention) return null;
  return (
    <div className="flex flex-wrap gap-2" aria-label="Needs attention" data-testid="systems-attention">
      <AttentionChip count={attention.incompleteCheckpoints} label="incomplete checkpoints" to="/admin/platform/runtime" warn />
      <AttentionChip count={attention.pendingDeletionRequests} label="pending deletion requests" to="/admin/inbox/deletion-requests" />
      <AttentionChip count={attention.newFeedback} label="new feedback" to="/admin/inbox/feedback" />
    </div>
  );
}

/** The route: superadmins only, everyone else goes home. */
export function SystemsRoute() {
  const session = useAuthSession();
  const [ws, setWs] = useAdminScope();
  if (!session?.user.isSuperadmin) return <Navigate to="/" replace />;
  return <SystemsPage key={ws ?? "all"} initialQuery={{ workspaceId: ws }} onScope={setWs} />;
}
